// import * as firebaseui from 'firebaseui'

import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  type Auth,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import environment from 'app/config/environment';
import {
  getNativeAuthToken,
  isEmbeddedInNativeShell,
  requestAppLogout,
} from './webviewAuth';

let cachedApp: FirebaseApp | null = null;
let cachedAuth: Auth | null = null;

function createFirebaseApp(): FirebaseApp {
  const firebaseConfig = {
    apiKey: environment.firebase.FIREBASE_API_KEY,
    authDomain: environment.firebase.FIREBASE_AUTH_DOMAIN,
    projectId: environment.firebase.FIREBASE_PROJECT_ID,
    storageBucket: environment.firebase.FIREBASE_STORAGE_BUCKET,
    messagingSenderId: environment.firebase.FIREBASE_MESSAGING_SENDER_ID,
    appId: environment.firebase.FIREBASE_APP_ID,
    measurementId: environment.firebase.FIREBASE_MEASUREMENT_ID,
    vapidKey: environment.firebase.FIREBASE_VAPID_KEY,
  };

  if (!getApps().length) {
    return initializeApp(firebaseConfig);
  }
  return getApp();
}

export function getFirebaseApp(): FirebaseApp {
  if (!cachedApp) {
    cachedApp = createFirebaseApp();
  }
  return cachedApp;
}

export function getFirebaseAuth(): Auth {
  if (!cachedAuth) {
    cachedAuth = getAuth(getFirebaseApp());
  }
  return cachedAuth;
}

const firebaseSignUpWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  const response = await createUserWithEmailAndPassword(
    getFirebaseAuth(),
    email,
    password
  );
  return response;
};

const firebaseSignInWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  const response = await signInWithEmailAndPassword(
    getFirebaseAuth(),
    email,
    password
  );
  return response;
};

// TOOO: Uncomment this if you want to enable FirebaseUI's sign in with email and password
// const firebaseSignInWithEmailPasswordUI = async (email: string, password: string) => {
//   // const app = initializeApp(firebaseConfig);
//   const app = firebase.initializeApp(firebaseConfig);
//   const auth = getAuth(app);

//   let ui = firebaseui.auth.AuthUI.getInstance();

//   if (!ui) {
//     ui = new firebaseui.auth.AuthUI(auth);
//   }

//   ui.start('#firebaseui-auth-container', {
//     signInOptions: [
//       firebase.auth.EmailAuthProvider.PROVIDER_ID
//     ],
//     // Other config options...
//   });
// }

const getFirebaseAccessToken = async () => {
  // Embedded in the native shell there is no Firebase session here; the shell is already
  // signed in and supplies its own ID token, which this backend accepts unchanged.
  if (isEmbeddedInNativeShell()) {
    return getNativeAuthToken();
  }

  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      getFirebaseAuth(),
      (user) => {
        unsubscribe();
        if (user) {
          user.getIdToken().then(resolve).catch(reject);
        } else {
          resolve(null);
        }
      },
      reject
    );
  });
};

const firebaseSignOut = async () => {
  if (isEmbeddedInNativeShell()) requestAppLogout();
  const response = await getFirebaseAuth().signOut();
  return response;
};

export {
  firebaseSignUpWithEmailAndPassword,
  firebaseSignInWithEmailAndPassword,
  // firebaseSignInWithEmailPasswordUI,
  getFirebaseAccessToken,
  firebaseSignOut,
};
